import { getMultiSelectorPanelConfig } from "app/pages/sales-intelligence/common-components/MultiSelector/helpers/multiSelectorPanel/helpers";
import type { TypeOfSelectors } from "app/pages/sales-intelligence/common-components/MultiSelector/types";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import { unknownToFetchError } from "app/pages/sales-intelligence/utils/errors/fetchError";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import {
    setWorkspacesListAction,
    toggleRightBar,
} from "app/pages/workspace/sales/sub-modules/common/store/action-creators";
import { isForbiddenSalesWorkspaceListResponseError } from "app/pages/sales-intelligence/utils/errors/forbiddenSalesWorkspaceList";
import { getSiteDataFromResponse } from "app/services/sitesService/sitesUtils";
import SalesWorkspaceApiService from "app/services/workspaces/salesWorkspaceApiService";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import { fetchOpportunityListsAsync } from "../../opportunities/store/action-creators";
import { selectHasOpportunityLists } from "../../opportunities/store/selectors";
import type { OpportunityListType } from "../../opportunities/types";

import * as actionCreators from "./action-creators";
import { setMultiSelectorPanel } from "./action-creators";

// This is legacy workspaces fetch that includes opportunity lists and saved searches.
// Should be removed asap and split on API side
export const fetchWorkspacesThunk = () => async (dispatch: ThunkDispatchCommon) => {
    dispatch(actionCreators.fetchLegacyWorkspaces.request());

    const api = new SalesWorkspaceApiService();

    try {
        const workspaces = await api.getWorkspaces();
        const safeWorkspaces = Array.isArray(workspaces) ? workspaces : [];
        const [first] = safeWorkspaces;

        if (typeof first === "undefined") {
            throw new Error("Empty workspace was not handled");
        }

        dispatch(actionCreators.setWorkspaceId(first.workspaceId));

        return {
            fetchOpportunityListAction: dispatch(
                fetchOpportunityListsAsync.success(first.opportunityLists as unknown as OpportunityListType[]),
            ),
            fetchLegacyWorkspacesAction: dispatch(actionCreators.fetchLegacyWorkspaces.success()),
            setWorkspacesListAction: dispatch(setWorkspacesListAction(safeWorkspaces)),
        };
    } catch (e: unknown) {
        if (!isForbiddenSalesWorkspaceListResponseError(e)) {
            const err = e instanceof Error ? e : new Error(String(e));
            trackError(err);
        }

        const fetchError = unknownToFetchError(e);

        return {
            fetchOpportunityListAction: dispatch(fetchOpportunityListsAsync.failure(fetchError)),
            fetchLegacyWorkspacesAction: dispatch(actionCreators.fetchLegacyWorkspaces.failure(fetchError)),
        };
    }
};

export const fetchWorkspacesIfMissingOpportunityListsThunk = () => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const hasOpportunityLists = selectHasOpportunityLists(getState());
        if (hasOpportunityLists) {
            return;
        }

        return dispatch(fetchWorkspacesThunk());
    };
};

export const fetchSimilarWebsitesThunk =
    (domain: string, size?: number) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(actionCreators.fetchSimilarWebsitesAsync.request());

        try {
            const similarWebsites = await deps.si.api.fetchSimilarWebsites(domain, size);

            dispatch(
                actionCreators.fetchSimilarWebsitesAsync.success({
                    domain,
                    similarWebsites,
                }),
            );
        } catch (e) {
            dispatch(actionCreators.fetchSimilarWebsitesAsync.failure(e));
        }
    };

export const fetchWebsiteInfoThunk =
    (domain: string) => async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(actionCreators.fetchWebsitesInfoAsync.request());

        try {
            const response = await deps.si.api.fetchWebsiteInfo(domain);
            const info = getSiteDataFromResponse(response, domain) || {};

            dispatch(
                actionCreators.fetchWebsitesInfoAsync.success({
                    info,
                    domain,
                }),
            );
        } catch (e) {
            dispatch(actionCreators.fetchWebsitesInfoAsync.failure(e));
        }
    };

/**
 * @deprecated Use `useExportQuotaQuery` hook instead.
 * This thunk is kept for legacy Redux integration only.
 */
export const fetchExcelQuotaThunk =
    () => async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(actionCreators.fetchExcelQuota.request());

        try {
            let excelQuota;

            if (deps.si.settingsHelper.isDataCreditsEnabled) {
                excelQuota = await quotaQueryClient.getExportQuota();
            } else {
                excelQuota = await deps.si.api.quota.fetchExcelQuota();
            }

            dispatch(actionCreators.fetchExcelQuota.success(excelQuota));
        } catch (e) {
            dispatch(actionCreators.fetchExcelQuota.failure(e));
        }
    };

export const setMultiSelectorPanelThunk =
    (panel: TypeOfSelectors | null) => (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const {
            salesWorkspace: { common },
        } = getState();

        if (common.isRightBarOpen) {
            dispatch(toggleRightBar(false));
        }

        dispatch(setMultiSelectorPanel({ type: panel, ...getMultiSelectorPanelConfig(panel) }));
    };
